import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';

import cookie from 'react-cookies';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import ExternalRedirect from './ExternalRedirect';
import SetTokenPage from './SetToken';

import NewPassword from '../components/Auth/NewPassword';
import PhoneVerification from '../components/Auth/PhoneVerification';
import Register from '../components/Auth/Register';
import ResetPassword from '../components/Auth/ResetPassword';
import SelectChannel from '../components/Auth/SelectChannel';
import SignIn from '../components/Auth/SignIn';
import Layout from '../components/common/Layout';
import { UserContext } from '../context/UserContext';
import { cleanUpSessionData } from '../helpers/applicationHelp';
import {
  changePassword,
  phoneVerificationRegister,
  phoneVerificationRegisterResendCode,
  phoneVerificationReset,
  phoneVerificationSignInResendCode,
  phoneVerificationResetResendCode,
  register,
  resetPassword,
  signIn,
  optChannelSelect,
  otpVerificationSignIn,
} from '../service/auth';

const Auth = () => {
  const location = useLocation();
  const [intentId, setIntentID] = useState(null);
  const { profile } = useContext(UserContext);
  const [phone, setPhone] = useState('+1-___-___-____');
  const [password, setPassword] = useState(null);
  const [channels, setChannels] = useState([]);
  const [otpChannel, setOtpChannel] = useState(null);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const token = query.get('token');
  const destination = query.get('app');

  useEffect(() => {
    if (profile.email) {
      if (destination === '/admin') {
        const token = cookie.load('lendistry_tokens_access');
        const refreshToken = cookie.load('lendistry_tokens_refresh');
        navigate({
          pathname: '/auth/success',
          search: `?token=${token}&refresh=${refreshToken}&app=/admin`,
        });
      }
      navigate(destination ?? '/dashboard');
    }
  }, [destination, navigate, profile]);

  const onRegisterSubmit = useCallback(async (values) => {
    const response = await register({ ...values, phone: values.phone.replaceAll('-', '') });
    setIntentID(response?.data?.intentId);
    setPhone(values.phone);
  }, []);

  const onPhoneVerificationSubmit = useCallback(
    async ({ code }) => {
      let response = null;
      if (!!password) {
        response = await otpVerificationSignIn({
          code,
          intent: intentId,
          password,
          otpChannel: otpChannel ? otpChannel : 'PHONE_SMS',
        });
      } else {
        response = await phoneVerificationRegister({ code, intentId });
      }
      const { token, refreshToken } = response.data;

      if (destination === '/admin') {
        return navigate({
          pathname: '/auth/success',
          search: `?token=${token}&refresh=${refreshToken}&app=/admin`,
        });
      }
      navigate(`/token?token=${token}&refresh=${refreshToken}&app=${destination || '/dashboard'}`);
    },
    [destination, navigate, intentId, password]
  );

  const onPhoneVerificationRecoverySubmit = useCallback(
    async ({ code }) => {
      await phoneVerificationReset({ code, intentId });
    },
    [intentId]
  );

  const onSignInSubmit = useCallback(async (values) => {
    //we clear any previous data that might interfere
    cleanUpSessionData();
    const response = await signIn(values);
    setPhone(response?.data?.phone);
    setIntentID(response?.data?.intentId);
    setPassword(values.password);
    setEmail(values.email);
    setChannels(response?.data?.channelsAvailable);
    return response;
  }, []);

  const onChannelSubmit = useCallback(
    async ({ channel }) => {
      await optChannelSelect({ intent: intentId, password, otpChannel: channel });
      setOtpChannel(channel);
    },
    [intentId, password]
  );

  const onPassResetSubmit = useCallback(async (values) => {
    const response = await resetPassword(values);
    setIntentID(response?.data?.intentId);
    setPhone(response?.data?.phone);
  }, []);

  const oneNewPasswordSubmit = useCallback(
    async (values) => {
      await changePassword({ ...values, intentId });
    },
    [intentId]
  );

  const onResendCode = useCallback(async () => {
    !!password
      ? await phoneVerificationSignInResendCode({ intent: intentId })
      : await phoneVerificationRegisterResendCode({ intentId });
  }, [intentId, password]);

  const onResendCodeRecovery = useCallback(async () => {
    await phoneVerificationResetResendCode({ intent: intentId });
  }, [intentId]);

  if (token) {
    return <SetTokenPage />;
  }

  return (
    <Layout>
      <Routes>
        <Route
          path="/register"
          element={
            <Register
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={onRegisterSubmit}
            />
          }
        />
        <Route
          path="sign-in"
          element={
            <SignIn
              onSubmit={onSignInSubmit}
              className={destination === '/admin' ? 'admin' : 'auth'}
            />
          }
        />
        <Route
          path="/reset-password"
          element={
            <ResetPassword
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={onPassResetSubmit}
            />
          }
        />
        <Route
          path="/phone-verification"
          element={
            <PhoneVerification
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={onPhoneVerificationSubmit}
              phone={phone}
              email={email}
              password={password}
              onResendCode={onResendCode}
              otpChannel={otpChannel}
            />
          }
        />
        <Route
          path="/select-channel"
          element={
            <SelectChannel
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={onChannelSubmit}
              email={email}
              phone={phone}
              password={password}
              channels={channels}
            />
          }
        />
        <Route
          path="/phone-password-recovery"
          element={
            <PhoneVerification
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={onPhoneVerificationRecoverySubmit}
              phone={phone}
              onResendCode={onResendCodeRecovery}
              recovery
            />
          }
        />
        <Route
          path="/new-password"
          element={
            <NewPassword
              className={destination === '/admin' ? 'admin' : 'auth'}
              onSubmit={oneNewPasswordSubmit}
            />
          }
        />
        {/* <Route
          path="/success"
          // exact
          render={() => <ExternalRedirect link={`${process.env.REACT_APP_ADMIN_URL}`} />}
        /> */}
      </Routes>
    </Layout>
  );
};

export default Auth;
